// scanService.js - Fixed to consistently use idToken for API authentication

// PRD-151 WP7: first-party telemetry. Safe to import here — telemetryService
// imports only 'react' + 'react-router-dom' (no scanService dependency), so no
// circular import is created.
import { track as trackTelemetry } from './telemetryService';

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

// Helper to get auth token
// FIXED: Use idToken for API authentication (CIAM with OIDC scopes)
async function getAuthToken() {
  try {
    const msalInstance = window.msalInstance;
    if (!msalInstance) return null;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const request = {
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    };

    const response = await msalInstance.acquireTokenSilent(request);
    
    // FIXED: With OIDC scopes (openid, profile, email), use idToken for our API
    // The accessToken from these scopes is for Microsoft endpoints, not our API
    const token = response.idToken;
    
    console.log('Token acquired: idToken');
    
    return token;
  } catch (error) {
    console.log('Failed to acquire token:', error.message);
    return null;
  }
}

// Helper to get real user ID
function getUserId() {
  try {
    const msalInstance = window.msalInstance;
    if (!msalInstance) return 'anonymous';

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return 'anonymous';

    // Use localAccountId (unique user identifier from CIAM)
    return accounts[0].localAccountId || accounts[0].homeAccountId || 'anonymous';
  } catch (error) {
    console.log('Failed to get userId:', error.message);
    return 'anonymous';
  }
}

const scanService = {
  // Start SCRUTINIZE comprehensive scan
  startScrutinizeScan: async (target) => {
    try {
      console.log('Starting SCRUTINIZE scan for:', target);

      // PRD-151 WP7: scan kicked off. Enum-only meta — NEVER the target/domain.
      try { trackTelemetry('scan_start', { meta: { scanType: 'scrutinize' } }); } catch (err) { /* best-effort */ }

      const token = await getAuthToken();
      const userId = getUserId();
      
      console.log('Using userId:', userId);
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('Authorization header set');
      } else {
        console.warn('No token available - request will likely fail');
      }

      const response = await fetch(`${API_BASE_URL}/scan-scrutinize-start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          target,
          userId
        }),
      });

      console.log('SCRUTINIZE API response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to start SCRUTINIZE scan';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('SCRUTINIZE API error data:', errorData);
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
          console.error('Could not parse error response:', e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('SCRUTINIZE scan initiated:', data);
      return data;
    } catch (error) {
      console.error('Error starting SCRUTINIZE scan:', error);
      throw error;
    }
  },

  // Get SCRUTINIZE scan results
  getScrutinizeResult: async (scanId) => {
    try {
      const token = await getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/scan-scrutinize-result?scanId=${scanId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Scan not yet complete
        }
        let errorMessage = 'Failed to get SCRUTINIZE result';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting SCRUTINIZE result:', error);
      throw error;
    }
  },

  // Get scan results (works for both fast and SCRUTINIZE scans)
  getScanResult: async (scanId) => {
    try {
      const token = await getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/scan-result?scanId=${scanId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Scan not yet complete
        }
        let errorMessage = 'Failed to get scan result';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting scan result:', error);
      throw error;
    }
  },

  // List all scans
  listScans: async () => {
    try {
      const token = await getAuthToken();
      const userId = getUserId();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/scan-list?userId=${userId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to list scans';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing scans:', error);
      throw error;
    }
  },

  // Start fast scan
  startScan: async (target) => {
    try {
      const token = await getAuthToken();
      const userId = getUserId();

      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/scan-fast`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ target, userId }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to start scan';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error starting scan:', error);
      throw error;
    }
  },

  // Generate PDF report for a scan
  // Set force=true to regenerate even if cached PDF exists
  generatePDF: async (scanId, scanType = 'scrutinize', force = false) => {
    try {
      console.log(`Generating PDF for ${scanType} scan: ${scanId}${force ? ' (force regenerate)' : ''}`);

      const token = await getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/scan-generate-pdf`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ scanId, scanType, force }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate PDF';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('PDF generated:', data);
      return data;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  },

  // Download PDF - opens in new tab or triggers download
  // Set force=true to regenerate even if cached PDF exists
  downloadPDF: async (scanId, scanType = 'scrutinize', force = false) => {
    try {
      const result = await scanService.generatePDF(scanId, scanType, force);

      if (result.success && result.pdfUrl) {
        // Open PDF in new tab for download
        window.open(result.pdfUrl, '_blank');
        return result;
      } else {
        throw new Error('PDF URL not available');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  },
};

export default scanService;